import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { PermissionGroupDef } from "@/features/users/data/endpoints";
import {
  createPermissionGroup,
  deletePermissionGroup,
  listPermissionGroups,
  updatePermissionGroup,
  type CreatePermissionGroupInput,
  type UpdatePermissionGroupInput,
} from "@/features/users/services/apiPermissionCatalog";

export const permissionGroupKeys = {
  all: ["apiPermissionGroups"] as const,
  list: () => [...permissionGroupKeys.all, "list"] as const,
};

/**
 * Grupos de permisos desde Firestore (`api_permission_groups`).
 */
export function usePermissionGroups() {
  return useQuery<PermissionGroupDef[]>({
    queryKey: permissionGroupKeys.list(),
    queryFn: listPermissionGroups,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreatePermissionGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreatePermissionGroupInput) => createPermissionGroup(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: permissionGroupKeys.all });
    },
  });
}

export function useUpdatePermissionGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      docId,
      data,
    }: {
      docId: string;
      data: UpdatePermissionGroupInput;
    }) => updatePermissionGroup(docId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: permissionGroupKeys.all });
    },
  });
}

export function useDeletePermissionGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (docId: string) => deletePermissionGroup(docId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: permissionGroupKeys.all });
    },
  });
}
